import React, { useMemo } from "react";
import moment from "moment";
import { formatHm, formatPunch, statusLabel, statusPillClass, workLocationLabel } from "./attendanceUiHelpers";

const AttendanceHistoryPanel = ({ analyticsDaily, analyticsLoading, historySearch, onSearchChange }) => {
  const isRollup = Array.isArray(analyticsDaily?.rows);
  const personalRows = analyticsDaily?.days || [];
  const rollupRows = analyticsDaily?.rows || [];
  const todayStr = moment().format("YYYY-MM-DD");

  const rowsNewestFirst = useMemo(() => {
    if (isRollup) {
      return [...rollupRows].sort((a, b) => {
        if (a.date !== b.date) return moment(b.date).valueOf() - moment(a.date).valueOf();
        return (a.employeeName || "").localeCompare(b.employeeName || "");
      });
    }
    return [...personalRows].sort((a, b) => moment(b.date).valueOf() - moment(a.date).valueOf());
  }, [isRollup, rollupRows, personalRows]);

  const filtered = useMemo(() => {
    const q = (historySearch || "").trim().toLowerCase();
    if (!q) return rowsNewestFirst;
    return rowsNewestFirst.filter((r) => {
      const st = statusLabel(r.status).toLowerCase();
      const ds = moment(r.date).format("DD/MM/YYYY");
      const name = (r.employeeName || "").toLowerCase();
      const code = String(r.employeeCode || "").toLowerCase();
      const wl = workLocationLabel(r.workLocation).toLowerCase();
      return (
        st.includes(q) ||
        ds.includes(q) ||
        String(r.date).includes(q) ||
        name.includes(q) ||
        code.includes(q) ||
        wl.includes(q)
      );
    });
  }, [rowsNewestFirst, historySearch]);

  return (
    <div className="att-history card-border card-body">
      <div className="att-history__header">
        <h4 className="mb-1">History</h4>
        <p className="text-sm text-[#6f7d97] mb-4">
          {isRollup
            ? "Daily punch summary for all employees, including ex-employees through their last working day."
            : "View your punch-in and punch-out logs."}
        </p>
        <input
          className="search-input max-w-md"
          placeholder={isRollup ? "Search by name, code, date, or status" : "Search by date or status"}
          value={historySearch}
          onChange={(e) => onSearchChange(e.target.value)}
        />
      </div>

      {analyticsLoading ? (
        <div className="py-8 text-center text-sm text-[#6f7d97]">Loading…</div>
      ) : isRollup ? (
        <div className="attendance-log-table-wrap att-history__table-wrap att-history__table-wrap--rollup mt-4">
          <table className="attendance-log-table attendance-log-table--history attendance-log-table--rollup">
            <thead>
              <tr>
                <th>Date</th>
                <th>Employee</th>
                <th>Code</th>
                <th>Punch-In</th>
                <th>Punch-Out</th>
                <th>Worked Hours</th>
                <th>Status</th>
                <th>WFH / WFS</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((r) => {
                const isUpcoming = r.status === "upcoming";
                const dash = "—";
                return (
                  <tr key={`${r.date}-${r.userId}`}>
                    <td>
                      <span className="whitespace-nowrap">{moment(r.date).format("DD/MM/YYYY")}</span>
                      {r.date === todayStr ? (
                        <span className="att-today-dot ml-2 text-xs text-sky-600">• Today</span>
                      ) : null}
                    </td>
                    <td className="att-history__emp-cell">
                      {r.employeeName || dash}
                      {r.isExEmployee ? (
                        <span className="att-history__ex-badge" title={r.lastWorkingDate ? `Last working day: ${moment(r.lastWorkingDate).format("DD MMM YYYY")}` : "Ex-employee"}>
                          Ex
                        </span>
                      ) : null}
                    </td>
                    <td>{r.employeeCode || dash}</td>
                    <td>{isUpcoming ? dash : formatPunch(r.punchIn)}</td>
                    <td>{isUpcoming ? dash : formatPunch(r.punchOut)}</td>
                    <td>{isUpcoming ? dash : formatHm(r.workedMinutes)}</td>
                    <td>
                      {isUpcoming ? (
                        <span className="att-history__status-empty">{dash}</span>
                      ) : (
                        <span className={statusPillClass(r.status)}>{statusLabel(r.status)}</span>
                      )}
                    </td>
                    <td className="att-history__wl-cell">{workLocationLabel(r.workLocation)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {!filtered.length ? <div className="text-sm text-[#6f7d97] py-6">No rows in this range.</div> : null}
        </div>
      ) : (
        <div className="attendance-log-table-wrap att-history__table-wrap mt-4">
          <table className="attendance-log-table attendance-log-table--history">
            <thead>
              <tr>
                <th>Date</th>
                <th>Punch-In</th>
                <th>Punch-Out</th>
                <th>Worked Hours</th>
                {/* <th>Overtime</th> */}
                <th>Status</th>
                {/* <th>WFH / WFS</th> */}
              </tr>
            </thead>
            <tbody>
              {filtered.map((r) => {
                const isUpcoming = r.status === "upcoming";
                const dash = "—";
                return (
                  <tr key={r.date}>
                    <td>
                      <span className="whitespace-nowrap">{moment(r.date).format("DD/MM/YYYY")}</span>
                      {r.date === todayStr ? (
                        <span className="att-today-dot ml-2 text-xs text-sky-600">• Today</span>
                      ) : null}
                    </td>
                    <td>{isUpcoming ? dash : formatPunch(r.punchIn)}</td>
                    <td>{isUpcoming ? dash : formatPunch(r.punchOut)}</td>
                    <td>{isUpcoming ? dash : formatHm(r.workedMinutes)}</td>
                    {/* <td>{isUpcoming ? dash : formatHm(r.overtimeMinutes)}</td> */}
                    <td>
                      {isUpcoming ? (
                        <span className="att-history__status-empty">{dash}</span>
                      ) : (
                        <span className={statusPillClass(r.status)}>{statusLabel(r.status)}</span>
                      )}
                    </td>
                    {/* <td className="att-history__wl-cell">{workLocationLabel(r.workLocation)}</td> */}
                  </tr>
                );
              })}
            </tbody>
          </table>
          {!filtered.length ? <div className="text-sm text-[#6f7d97] py-6">No rows in this range.</div> : null}
        </div>
      )}
    </div>
  );
};

export default AttendanceHistoryPanel;
